import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { uploadDataUrl, fileUrl } from '../../api/fileStorage';
import HeroCropOverlay, { CropState } from './HeroCropOverlay';

/**
 * useHeroEdit — shared hook + presentational pieces for editing a project
 * hero (cover) image. Used by both Full Project (ProjectDetailPage) and
 * Mini-Project (MiniProjectDetailPage).
 *
 * Returns three values the caller passes through to PillarPageShell:
 *   - menu     → heroEditControls prop (floating button + dropdown + URL input)
 *   - overlay  → heroCropOverlay prop  (cropping UI, rendered full-bleed)
 *   - menuOpen → heroMenuOpen prop     (lets the shell allow dropdown overflow)
 *
 * The hook owns the full edit lifecycle: file validation, MinIO upload via
 * uploadDataUrl with cache-buster, crop state, dropdown / URL input state,
 * and re-cropping an existing hero (fetched as blob to dodge canvas taint).
 *
 * The caller supplies:
 *   - canEdit:           gate the entire UI off when false
 *   - currentHeroUrl:    drives conditional menu items (Edit Crop / Remove)
 *                        appear only when there's a hero to act on
 *   - uploadBucket:      MinIO bucket key (e.g. 'heroes', 'mini-heroes')
 *   - uploadEntityKey:   entity GUID used in the storage path
 *   - onPersist(url):    persist the new hero URL (caller's data layer)
 *   - onRemove():        clear the hero (caller's data layer)
 *
 * Style literals on the floating button / menu / URL input are carried
 * over verbatim from the legacy inline implementation to preserve visual
 * fidelity on photo backgrounds. Tokenization is a separate cleanup.
 */

const MAX_RAW_FILE_BYTES = 10 * 1024 * 1024;

export interface UseHeroEditOptions {
  canEdit: boolean;
  currentHeroUrl: string | null | undefined;
  uploadBucket: string;
  uploadEntityKey: string | null | undefined;
  onPersist: (url: string) => void;
  onRemove: () => void;
}

export interface UseHeroEditResult {
  menu: React.ReactNode;
  overlay: React.ReactNode;
  menuOpen: boolean;
}

export function useHeroEdit(opts: UseHeroEditOptions): UseHeroEditResult {
  const { canEdit, currentHeroUrl, uploadBucket, uploadEntityKey, onPersist, onRemove } = opts;
  const { t } = useAppTranslation('pages');

  const [cropState, setCropState] = useState<CropState | null>(null);
  const [heroMenuOpen, setHeroMenuOpen] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlDraft, setUrlDraft] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const heroMenuRef = useRef<HTMLDivElement>(null);

  // Close dropdown on click-outside.
  useEffect(() => {
    if (!heroMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (heroMenuRef.current && !heroMenuRef.current.contains(e.target as Node)) {
        setHeroMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [heroMenuOpen]);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert(t('heroEdit.invalidImageType'));
      return;
    }
    if (file.size > MAX_RAW_FILE_BYTES) {
      alert(
        t('heroEdit.imageTooLarge').replace('{size}', (file.size / 1024 / 1024).toFixed(1)),
      );
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      setCropState({ src: objectUrl, naturalW: img.naturalWidth, naturalH: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      alert(t('heroEdit.failedToLoad'));
    };
    img.src = objectUrl;
    e.target.value = '';
  }, [t]);

  const handleCropConfirm = useCallback(
    (dataUrl: string) => {
      if (cropState) URL.revokeObjectURL(cropState.src);
      setCropState(null);
      // Upload cropped image to MinIO file storage; fall back to data URL on failure.
      if (uploadEntityKey) {
        const ts = Date.now();
        uploadDataUrl(dataUrl, uploadBucket, uploadEntityKey, `hero-${ts}.jpg`)
          .then((result) => {
            // Cache-buster so the browser always fetches the new image.
            const storedUrl = `${fileUrl(result.key)}?v=${ts}`;
            console.log('[Hero] Uploaded to MinIO:', storedUrl);
            onPersist(storedUrl);
          })
          .catch((err) => {
            console.warn('[Hero] MinIO upload failed, falling back to data URL:', err);
            onPersist(dataUrl);
          });
      } else {
        onPersist(dataUrl);
      }
    },
    [cropState, onPersist, uploadBucket, uploadEntityKey],
  );

  const handleCropCancel = useCallback(() => {
    if (cropState) URL.revokeObjectURL(cropState.src);
    setCropState(null);
  }, [cropState]);

  const handleUrlSubmit = useCallback(() => {
    const url = urlDraft.trim();
    if (!url) return;
    if (!url.startsWith('http://') && !url.startsWith('https://') && !url.startsWith('data:')) {
      alert(t('heroEdit.invalidUrl'));
      return;
    }
    // If it's a data URL, upload to MinIO first.
    if (url.startsWith('data:') && uploadEntityKey) {
      uploadDataUrl(url, uploadBucket, uploadEntityKey, 'hero.jpg')
        .then((result) => {
          onPersist(fileUrl(result.key));
        })
        .catch(() => {
          onPersist(url); // fallback
        });
    } else {
      onPersist(url);
    }
    setShowUrlInput(false);
    setUrlDraft('');
  }, [urlDraft, onPersist, uploadBucket, uploadEntityKey, t]);

  /** Open the crop overlay on the *existing* hero so the user can re-crop.
   *  Fetches as a blob to avoid canvas taint from cross-origin image URLs. */
  const handleEditHero = useCallback(() => {
    if (!currentHeroUrl) return;
    fetch(currentHeroUrl)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        const img = new Image();
        img.onload = () => {
          setCropState({
            src: blobUrl,
            naturalW: img.naturalWidth,
            naturalH: img.naturalHeight,
          });
        };
        img.onerror = () => {
          URL.revokeObjectURL(blobUrl);
          alert(t('heroEdit.failedToLoadEdit'));
        };
        img.src = blobUrl;
      })
      .catch(() => {
        alert(t('heroEdit.failedToLoadEdit'));
      });
  }, [currentHeroUrl, t]);

  // Overlay node — rendered separately at the hero-banner level via the
  // shell's heroCropOverlay prop. When canEdit is false or no crop is in
  // progress, this is null and the shell renders nothing.
  const overlay: React.ReactNode = canEdit && cropState
    ? <HeroCropOverlay crop={cropState} onConfirm={handleCropConfirm} onCancel={handleCropCancel} />
    : null;

  // Menu node — the floating "Edit cover photo" button, dropdown, URL input,
  // and hidden file input. Suppressed during cropping (the overlay covers it).
  const menu: React.ReactNode = canEdit ? (
    <>
      {!cropState && (
        <div
          ref={heroMenuRef}
          style={{ position: 'absolute', bottom: 10, insetInlineEnd: 10, zIndex: 50 }}
        >
          <button
            type="button"
            onClick={() => setHeroMenuOpen((v) => !v)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              background: '#fff',
              color: '#1e293b',
              border: 'none',
              borderRadius: 8,
              padding: '7px 14px',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              transition: 'background 0.15s',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = 'var(--text-primary)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = '#fff';
            }}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
              <circle cx="12" cy="13" r="4" />
            </svg>
            {t('heroEdit.editCoverPhoto')}
          </button>

          {heroMenuOpen && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                insetInlineEnd: 0,
                marginTop: 6,
                background: 'var(--bg-elevated)',
                backdropFilter: 'blur(8px)',
                borderRadius: 10,
                overflow: 'hidden',
                border: '1px solid var(--border-default)',
                boxShadow: '0 8px 28px rgba(0,0,0,0.5)',
                minWidth: 180,
                zIndex: 60,
              }}
            >
              <button
                type="button"
                onClick={() => {
                  fileInputRef.current?.click();
                  setHeroMenuOpen(false);
                }}
                style={heroMenuItemStyle}
              >
                {currentHeroUrl ? t('heroEdit.changePhoto') : t('heroEdit.uploadPhoto')}
              </button>
              {currentHeroUrl && (
                <button
                  type="button"
                  onClick={() => {
                    handleEditHero();
                    setHeroMenuOpen(false);
                  }}
                  style={heroMenuItemStyle}
                >
                  {t('heroEdit.editCrop')}
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setShowUrlInput((v) => !v);
                  setUrlDraft('');
                  setHeroMenuOpen(false);
                }}
                style={heroMenuItemStyle}
              >
                {t('heroEdit.pasteUrl')}
              </button>
              {currentHeroUrl && (
                <button
                  type="button"
                  onClick={() => {
                    onRemove();
                    setHeroMenuOpen(false);
                  }}
                  style={{
                    ...heroMenuItemStyle,
                    color: '#f87171',
                    borderBottom: 'none',
                  }}
                >
                  {t('heroEdit.removePhoto')}
                </button>
              )}
            </div>
          )}

          {showUrlInput && !heroMenuOpen && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                insetInlineEnd: 0,
                marginTop: 6,
                display: 'flex',
                gap: 6,
                maxWidth: 420,
              }}
            >
              <input
                type="text"
                value={urlDraft}
                onChange={(e) => setUrlDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleUrlSubmit();
                  if (e.key === 'Escape') {
                    setShowUrlInput(false);
                    setUrlDraft('');
                  }
                }}
                placeholder="https://example.com/image.jpg"
                autoFocus
                style={{
                  flex: 1,
                  background: 'rgba(0,0,0,0.70)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 6,
                  color: 'var(--text-secondary)',
                  fontSize: 12,
                  padding: '5px 10px',
                  outline: 'none',
                  minWidth: 180,
                }}
              />
              <button
                type="button"
                onClick={handleUrlSubmit}
                style={{
                  background: 'rgba(69,187,71,0.70)',
                  border: '1px solid var(--border-default)',
                  borderRadius: 6,
                  color: 'var(--text-primary)',
                  fontSize: 12,
                  fontWeight: 600,
                  padding: '5px 12px',
                  cursor: 'pointer',
                }}
              >
                {t('heroEdit.save')}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
    </>
  ) : null;

  return { menu, overlay, menuOpen: heroMenuOpen };
}

/* ------------------------------------------------------------------ */
/*  Hero dropdown menu item style                                      */
/* ------------------------------------------------------------------ */

const heroMenuItemStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  textAlign: 'start',
  background: 'transparent',
  border: 'none',
  borderBottom: '1px solid var(--border-subtle)',
  color: 'var(--text-tertiary)',
  fontSize: 13,
  fontWeight: 500,
  padding: '10px 16px',
  cursor: 'pointer',
  transition: 'background 0.15s, color 0.15s',
};
